import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Input, Select, Stack } from "@wato/ui";
import { useApi, useErrorMessage } from "../../api/context";
import { isApiError } from "../../api/client";
import type { Locale } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { SETUP_ROUTE } from "../../auth/guards";
import { AuthLayout, PageLoading } from "../../components/common";
import { SignupLegalLine } from "./SignupLegalLine";
import { SocialAuthButtons } from "./SocialAuthButtons";

const PASSWORD_MIN_LENGTH = 10;

// FR-AUTH-002 value sets (same as the API's SIGNUP_* lists).
const COUNTRIES = ["SA", "BH", "AE", "KW", "OM", "QA"] as const;
const INDUSTRIES = [
  "retail",
  "ecommerce",
  "food_beverage",
  "health_beauty",
  "education",
  "real_estate",
  "automotive",
  "travel_hospitality",
  "professional_services",
  "other",
] as const;
const TEAM_SIZES: ReadonlyArray<readonly [string, MessageKey]> = [
  ["1", "dashboard.auth.signup.teamSizes.solo"],
  ["2-5", "dashboard.auth.signup.teamSizes.small"],
  ["6-20", "dashboard.auth.signup.teamSizes.medium"],
  ["21-50", "dashboard.auth.signup.teamSizes.large"],
  ["51+", "dashboard.auth.signup.teamSizes.xlarge"],
];
const GOALS = ["sales", "support", "bookings", "marketing"] as const;

/** UX-29: a signed-in visitor sees who they are instead of a silent redirect. */
export function SignupPage() {
  const { state } = useAuth();
  if (state.status === "loading") return <PageLoading />;
  if (state.status === "setup") return <Navigate to={SETUP_ROUTE} replace />;
  if (state.status === "ready") return <SignedInNotice email={state.me.user.email} />;
  return <SignupForm />;
}

function SignedInNotice({ email }: { email: string }) {
  const { t } = useI18n();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <AuthLayout
      title={t("dashboard.auth.signup.signedInTitle")}
      subtitle={t("dashboard.auth.signup.signedInAs", { email })}
    >
      <Stack gap={4}>
        <p className="text-sm text-fg-muted">{t("dashboard.auth.signup.signedInHint")}</p>
        <Button onClick={() => void navigate("/")}>{t("dashboard.auth.signup.goToApp")}</Button>
        <Button variant="secondary" onClick={() => void navigate("/workspaces/new")}>
          {t("dashboard.auth.signup.createWorkspace")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            void signOut().then(() => navigate("/signup", { replace: true }));
          }}
        >
          {t("dashboard.shell.signOut")}
        </Button>
      </Stack>
    </AuthLayout>
  );
}

function SignupForm() {
  const { t, locale } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();
  const [form, setForm] = useState({
    email: "",
    password: "",
    businessName: "",
    country: "SA",
    industry: "retail",
    teamSize: "1",
    primaryGoal: "sales",
    preferredLanguage: locale as Locale,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The "check the details above" hint only fits a rejected form, not a rate limit.
  const [showFormHint, setShowFormHint] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
    businessName?: string;
  }>({});

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  // FR-SEC-017: at least 10 characters (counted in code points, same as the API).
  const passwordOk = [...form.password].length >= PASSWORD_MIN_LENGTH;

  async function submit(event: FormEvent) {
    event.preventDefault();
    const email = form.email.trim();
    const errors = {
      ...(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        ? {}
        : { email: t("dashboard.auth.signup.emailInvalid") }),
      ...(passwordOk ? {} : { password: t("dashboard.auth.signup.passwordShort") }),
      ...(form.businessName.trim()
        ? {}
        : { businessName: t("dashboard.auth.signup.businessNameRequired") }),
    };
    setFieldErrors(errors);
    setError(null);
    if (Object.keys(errors).length > 0) return;
    setBusy(true);
    try {
      await api.signup({ ...form, email });
      await navigate("/verify-email", { state: { email, sent: true } });
    } catch (caught) {
      setShowFormHint(!(isApiError(caught) && (caught.status === 429 || caught.status === 0)));
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("dashboard.auth.signup.title")}
      subtitle={t("dashboard.auth.signup.subtitle")}
      footer={
        <>
          {t("dashboard.auth.signup.haveAccount")}{" "}
          <Link className="font-semibold text-on-action-ghost underline" to="/login">
            {t("dashboard.auth.signup.signIn")}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        <Stack gap={4}>
          {error && (
            <Alert tone="error">
              {error} {showFormHint ? t("dashboard.auth.signup.errorNext") : null}
            </Alert>
          )}
          <Input
            type="email"
            dir="ltr"
            autoComplete="email"
            required
            label={t("dashboard.auth.email")}
            value={form.email}
            {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
            onChange={(e) => set("email")(e.target.value)}
          />
          <Input
            type="password"
            autoComplete="new-password"
            required
            label={t("dashboard.auth.password")}
            hint={t("dashboard.auth.signup.passwordHint")}
            {...(fieldErrors.password ? { error: fieldErrors.password } : {})}
            value={form.password}
            onChange={(e) => set("password")(e.target.value)}
          />
          <Input
            required
            autoComplete="organization"
            label={t("dashboard.auth.signup.businessName")}
            value={form.businessName}
            {...(fieldErrors.businessName ? { error: fieldErrors.businessName } : {})}
            onChange={(e) => set("businessName")(e.target.value)}
          />
          <details className="group rounded-md border border-line p-3">
            <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold">
              {t("dashboard.auth.signup.moreInfo")}
              <span
                aria-hidden="true"
                data-testid="signup-more-chevron"
                className="transition-transform group-open:rotate-180"
              >
                ▾
              </span>
            </summary>
            <Stack gap={4}>
              <p className="pt-2 text-sm text-fg-muted">
                {t("dashboard.auth.signup.moreInfoHint")}
              </p>
              <Select
                label={t("dashboard.auth.signup.country")}
                value={form.country}
                onChange={(e) => set("country")(e.target.value)}
                options={COUNTRIES.map((c) => ({
                  value: c,
                  label: t(`dashboard.auth.signup.countries.${c}`),
                }))}
              />
              <Select
                label={t("dashboard.auth.signup.industry")}
                value={form.industry}
                onChange={(e) => set("industry")(e.target.value)}
                options={INDUSTRIES.map((i) => ({
                  value: i,
                  label: t(`dashboard.auth.signup.industries.${i}`),
                }))}
              />
              <Select
                label={t("dashboard.auth.signup.teamSize")}
                value={form.teamSize}
                onChange={(e) => set("teamSize")(e.target.value)}
                options={TEAM_SIZES.map(([value, key]) => ({ value, label: t(key) }))}
              />
              <Select
                label={t("dashboard.auth.signup.primaryGoal")}
                value={form.primaryGoal}
                onChange={(e) => set("primaryGoal")(e.target.value)}
                options={GOALS.map((g) => ({
                  value: g,
                  label: t(`dashboard.auth.signup.goals.${g}`),
                }))}
              />
              <Select
                label={t("dashboard.auth.signup.preferredLanguage")}
                value={form.preferredLanguage}
                onChange={(e) => set("preferredLanguage")(e.target.value)}
                options={(["ar", "en"] as const).map((l) => ({
                  value: l,
                  label: t(`dashboard.auth.signup.languages.${l}`),
                }))}
              />
            </Stack>
          </details>
          <Button type="submit" fullWidth loading={busy}>
            {t("dashboard.auth.signup.submit")}
          </Button>
          <SignupLegalLine />
          <SocialAuthButtons />
        </Stack>
      </form>
    </AuthLayout>
  );
}
